/**
 * TradingChart Component
 * 
 * Professional TradingView-style charting component
 * 
 * Version: 6.2.0
 */
import React, { useState, useRef, useEffect, useCallback } from 'react'
import './TradingChart.css'

// Types
interface CandleData {
  time: number
  open: number
  high: number
  low: number
  close: number
  volume: number
}

interface ChartConfig {
  showVolume: boolean
  showGrid: boolean
  showCrosshair: boolean
  indicators: string[]
  timeframe: string
}

type ChartType = 'candlestick' | 'line' | 'area'
type DrawingTool = 'crosshair' | 'trendline' | 'horizontal' | 'fibonacci' | 'none'

interface TradingChartProps {
  data: CandleData[]
  type?: ChartType
  config?: Partial<ChartConfig>
  onTimeframeChange?: (timeframe: string) => void
  onIndicatorToggle?: (indicator: string) => void
  className?: string
}

// Generate sample data
const generateSampleData = (count: number = 100): CandleData[] => {
  const data: CandleData[] = []
  let basePrice = 25000
  const now = Date.now()
  
  for (let i = 0; i < count; i++) {
    const volatility = Math.random() * 100 - 50
    const open = basePrice + volatility
    const close = open + (Math.random() * 60 - 30)
    const high = Math.max(open, close) + Math.random() * 30
    const low = Math.min(open, close) - Math.random() * 30
    const volume = Math.random() * 1000000 + 500000
    
    data.push({
      time: now - (count - i) * 60000 * 5, // 5 minute candles
      open: Math.round(open * 100) / 100,
      high: Math.round(high * 100) / 100,
      low: Math.round(low * 100) / 100,
      close: Math.round(close * 100) / 100,
      volume: Math.round(volume)
    })
    
    basePrice = close
  }
  return data
}

const TradingChart: React.FC<TradingChartProps> = ({
  data,
  type = 'candlestick',
  config,
  onTimeframeChange,
  onIndicatorToggle,
  className = ''
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [dimensions, setDimensions] = useState({ width: 800, height: 500 })
  const [hoveredCandle, setHoveredCandle] = useState<CandleData | null>(null)
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 })
  const [drawingTool, setDrawingTool] = useState<DrawingTool>('crosshair')
  
  const [chartConfig, setChartConfig] = useState<ChartConfig>({
    showVolume: true,
    showGrid: true,
    showCrosshair: true,
    indicators: [],
    timeframe: '5m',
    ...config
  })
  
  const timeframes = ['1m', '5m', '15m', '30m', '1H', '4H', '1D', '1W']
  // All 110 TradingView indicators for chart overlay
  // All 110 TradingView Built-in/Free Indicators for chart overlay
  const indicators = [
    // ========== MOMENTUM (20) ==========
    'RSI', 'Stochastic', 'Stochastic RSI', 'MACD', 'CCI', 'Momentum', 'ROC', 
    'Williams %R', 'Ultimate Oscillator', 'Aroon', 'Awesome Oscillator', 'KST',
    'Money Flow Index', 'Chaikin Oscillator', 'PPO', 'PPO Histogram', 
    'TSI', 'RVI', 'CMO', 'DPO',
    
    // ========== TREND (19) ==========
    'SMA', 'EMA', 'WMA', 'VWMA', 'Hull MA', 'Supertrend', 'Parabolic SAR',
    'ADX', 'ADX +DI', 'ADX -DI', 'Ichimoku Cloud', 'VWAP', 
    'Double EMA', 'Triple EMA', 'TRIX', 'ALMA', 'KAMA', 
    'Vortex Indicator +', 'Vortex Indicator -',
    
    // ========== VOLATILITY (15) ==========
    'Bollinger Upper Band', 'Bollinger Middle Band', 'Bollinger Lower Band',
    'Bollinger Bandwidth', 'Bollinger Squeeze', 'ATR',
    'Keltner Upper', 'Keltner Lower', 
    'Donchian Upper', 'Donchian Lower', 'Donchian Middle',
    'Historical Volatility', 'Standard Deviation', 'Mass Index', 'Ulcer Index',
    
    // ========== VOLUME (18) ==========
    'OBV', 'Volume Profile', 'AD', 'CMF', 'EMV', 'VPT', 'NVI', 'PVI',
    'Force Index', 'Klinger Volume Oscillator', 'Volume ROC',
    'VWAP Daily', 'VWAP Session', 'AD Line', 'MFI Raw',
    'Trade Volume Index', 'Volume Delta',
    
    // ========== PIVOTS & FIBONACCI (8) ==========
    'Fibonacci Retracement', 'Fibonacci Extension',
    'Pivot Classic R1', 'Pivot Classic S1',
    'Pivot Camarilla R1', 'Pivot Camarilla S1',
    'Pivot Fibonacci R1', 'Pivot Fibonacci S1',
    
    // ========== ICHIMOKU (5) ==========
    'Tenkan-Sen', 'Kijun-Sen', 'Senkou Span A', 'Senkou Span B', 'Chikou Span',
    
    // ========== AROON (3) ==========
    'Aroon Up', 'Aroon Down', 'Aroon Oscillator',
    
    // ========== STOCHASTIC (3) ==========
    'Stochastic %K', 'Stochastic %D', 'StochRSI %K',
    
    // ========== MACD (4) ==========
    'MACD Line', 'MACD Signal', 'MACD Histogram', 'MACD Crossover',
    
    // ========== RSI (3) ==========
    'RSI Raw', 'RSI Overbought Line', 'RSI Oversold Line',
    
    // ========== SUPERTREND (3) ==========
    'Supertrend Upper', 'Supertrend Lower', 'Supertrend Direction',
    
    // ========== CUSTOM (9) ==========
    'Price', 'Volume', 'Candle Pattern',
    'Open Price', 'High Price', 'Low Price', 'Close Price',
    'HL2', 'HLC3', 'OHLC4'
  ]

  // Handle resize
  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect()
        setDimensions({ width: rect.width, height: rect.height })
      }
    }
    
    updateDimensions()
    window.addEventListener('resize', updateDimensions)
    return () => window.removeEventListener('resize', updateDimensions)
  }, [])

  // Calculate price range
  const { minPrice, maxPrice, priceRange } = React.useMemo(() => {
    if (data.length === 0) return { minPrice: 0, maxPrice: 100, priceRange: 100 }
    
    const lows = data.map(d => d.low)
    const highs = data.map(d => d.high)
    const min = Math.min(...lows)
    const max = Math.max(...highs)
    const padding = (max - min) * 0.1
    
    return {
      minPrice: min - padding,
      maxPrice: max + padding,
      priceRange: max - min + padding * 2
    }
  }, [data])

  // Drawing function
  const drawChart = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas || data.length === 0) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const { width, height } = dimensions
    const chartHeight = chartConfig.showVolume ? height * 0.75 : height - 60
    const volumeHeight = chartConfig.showVolume ? height * 0.2 : 0
    const padding = { top: 20, right: 80, bottom: chartConfig.showVolume ? volumeHeight + 10 : 30, left: 10 }
    const chartWidth = width - padding.left - padding.right
    const candleWidth = chartWidth / data.length
    const bodyWidth = Math.max(2, candleWidth * 0.7)

    // Clear canvas
    ctx.clearRect(0, 0, width, height)
    
    // Draw background
    ctx.fillStyle = getComputedStyle(document.documentElement).getPropertyValue('--bg-primary').trim() || '#0f172a'
    ctx.fillRect(0, 0, width, height)

    // Draw grid
    if (chartConfig.showGrid) {
      ctx.strokeStyle = 'rgba(148, 163, 184, 0.1)'
      ctx.lineWidth = 1
      
      // Horizontal lines
      for (let i = 0; i <= 5; i++) {
        const y = padding.top + (chartHeight - padding.top - padding.bottom) * (i / 5)
        ctx.beginPath()
        ctx.moveTo(padding.left, y)
        ctx.lineTo(width - padding.right, y)
        ctx.stroke()
      }
      
      // Vertical lines
      for (let i = 0; i <= data.length; i += 10) {
        const x = padding.left + i * candleWidth
        ctx.beginPath()
        ctx.moveTo(x, padding.top)
        ctx.lineTo(x, chartHeight - padding.bottom)
        ctx.stroke()
      }
    }

    // Helper functions
    const priceToY = (price: number) => {
      return padding.top + (1 - (price - minPrice) / priceRange) * (chartHeight - padding.top - padding.bottom)
    }

    const timeToX = (time: number) => {
      const index = data.findIndex(d => d.time === time)
      return padding.left + index * candleWidth + candleWidth / 2
    }

    // Draw candles
    data.forEach((candle, index) => {
      const x = padding.left + index * candleWidth + candleWidth / 2
      const isGreen = candle.close >= candle.open
      const color = isGreen ? '#10b981' : '#ef4444'
      
      // Wick
      ctx.strokeStyle = color
      ctx.lineWidth = 1
      ctx.beginPath()
      ctx.moveTo(x, priceToY(candle.high))
      ctx.lineTo(x, priceToY(candle.low))
      ctx.stroke()
      
      // Body
      const openY = priceToY(candle.open)
      const closeY = priceToY(candle.close)
      const bodyTop = Math.min(openY, closeY)
      const bodyHeight = Math.max(1, Math.abs(openY - closeY))
      
      if (type === 'candlestick') {
        ctx.fillStyle = color
        ctx.fillRect(x - bodyWidth / 2, bodyTop, bodyWidth, bodyHeight)
      } else if (type === 'line' || type === 'area') {
        ctx.fillStyle = color
        ctx.beginPath()
        ctx.arc(x, closeY, 2, 0, Math.PI * 2)
        ctx.fill()
      }
    })

    // Draw line/area chart
    if (type === 'line' || type === 'area') {
      ctx.strokeStyle = '#6366f1'
      ctx.lineWidth = 2
      ctx.beginPath()
      
      data.forEach((candle, index) => {
        const x = padding.left + index * candleWidth + candleWidth / 2
        const y = priceToY(candle.close)
        
        if (index === 0) {
          ctx.moveTo(x, y)
        } else {
          ctx.lineTo(x, y)
        }
      })
      
      ctx.stroke()
      
      if (type === 'area') {
        const lastX = padding.left + (data.length - 1) * candleWidth + candleWidth / 2
        ctx.lineTo(lastX, chartHeight - padding.bottom)
        ctx.lineTo(padding.left + candleWidth / 2, chartHeight - padding.bottom)
        ctx.closePath()
        ctx.fillStyle = 'rgba(99, 102, 241, 0.2)'
        ctx.fill()
      }
    }

    // Draw volume bars
    if (chartConfig.showVolume) {
      const maxVolume = Math.max(...data.map(d => d.volume))
      const volumeY = chartHeight + 10
      
      data.forEach((candle, index) => {
        const x = padding.left + index * candleWidth + candleWidth / 2
        const isGreen = candle.close >= candle.open
        const barHeight = (candle.volume / maxVolume) * (volumeHeight - 20)
        
        ctx.fillStyle = isGreen ? 'rgba(16, 185, 129, 0.3)' : 'rgba(239, 68, 68, 0.3)'
        ctx.fillRect(x - bodyWidth / 2, volumeY + volumeHeight - 20 - barHeight, bodyWidth, barHeight)
      })
    }

    // Draw price scale
    ctx.fillStyle = getComputedStyle(document.documentElement).getPropertyValue('--text-muted').trim() || '#64748b'
    ctx.font = '11px monospace'
    ctx.textAlign = 'left'
    
    for (let i = 0; i <= 5; i++) {
      const price = maxPrice - (priceRange * i / 5)
      const y = padding.top + (chartHeight - padding.top - padding.bottom) * (i / 5)
      ctx.fillText(price.toFixed(2), width - padding.right + 5, y + 4)
    }

    // Draw crosshair
    if (chartConfig.showCrosshair && mousePos.x > 0 && mousePos.y > 0) {
      ctx.strokeStyle = 'rgba(148, 163, 184, 0.5)'
      ctx.lineWidth = 1
      ctx.setLineDash([5, 5])
      
      // Vertical line
      ctx.beginPath()
      ctx.moveTo(mousePos.x, padding.top)
      ctx.lineTo(mousePos.x, chartHeight - padding.bottom)
      ctx.stroke()
      
      // Horizontal line
      ctx.beginPath()
      ctx.moveTo(padding.left, mousePos.y)
      ctx.lineTo(width - padding.right, mousePos.y)
      ctx.stroke()
      
      ctx.setLineDash([])
      
      // Price label
      const priceAtY = maxPrice - ((mousePos.y - padding.top) / (chartHeight - padding.top - padding.bottom)) * priceRange
      ctx.fillStyle = '#6366f1'
      ctx.fillRect(width - padding.right, mousePos.y - 10, 75, 20)
      ctx.fillStyle = '#fff'
      ctx.fillText(priceAtY.toFixed(2), width - padding.right + 5, mousePos.y + 4)
    }

    // Draw indicators (All 110 TradingView Built-in/Free Indicators)
    const indicatorColors: Record<string, string> = {
      // Momentum
      'RSI': '#f59e0b', 'Stochastic': '#22c55e', 'Stochastic RSI': '#10b981',
      'MACD': '#06b6d4', 'CCI': '#14b8a6', 'Momentum': '#f97316',
      'ROC': '#ef4444', 'Williams %R': '#dc2626', 'Ultimate Oscillator': '#7c3aed',
      'Aroon': '#2563eb', 'Awesome Oscillator': '#059669', 'KST': '#db2777',
      'Money Flow Index': '#ca8a04', 'Chaikin Oscillator': '#0891b2',
      'PPO': '#06b6d4', 'PPO Histogram': '#14b8a6', 'TSI': '#8b5cf6',
      'RVI': '#ec4899', 'CMO': '#f97316', 'DPO': '#a855f7',
      // Trend
      'SMA': '#3b82f6', 'EMA': '#8b5cf6', 'WMA': '#ec4899',
      'VWMA': '#06b6d4', 'Hull MA': '#f59e0b', 'Supertrend': '#22c55e',
      'Parabolic SAR': '#ef4444', 'ADX': '#a855f7', 'ADX +DI': '#22c55e',
      'ADX -DI': '#ef4444', 'Ichimoku Cloud': '#0ea5e9', 'VWAP': '#8b5cf6',
      'Double EMA': '#14b8a6', 'Triple EMA': '#f97316', 'TRIX': '#6366f1',
      'ALMA': '#ec4899', 'KAMA': '#14b8a6', 'Vortex Indicator +': '#22c55e',
      'Vortex Indicator -': '#ef4444',
      // Volatility
      'Bollinger Upper Band': '#f43f5e', 'Bollinger Middle Band': '#f59e0b',
      'Bollinger Lower Band': '#22c55e', 'Bollinger Bandwidth': '#a855f7',
      'Bollinger Squeeze': '#06b6d4', 'ATR': '#f97316',
      'Keltner Upper': '#ef4444', 'Keltner Lower': '#22c55e',
      'Donchian Upper': '#ef4444', 'Donchian Lower': '#22c55e',
      'Donchian Middle': '#f59e0b', 'Historical Volatility': '#7c3aed',
      'Standard Deviation': '#6366f1', 'Mass Index': '#ec4899', 'Ulcer Index': '#dc2626',
      // Volume
      'OBV': '#64748b', 'Volume Profile': '#0ea5e9', 'AD': '#2563eb',
      'CMF': '#059669', 'EMV': '#0891b2', 'VPT': '#ca8a04',
      'NVI': '#dc2626', 'PVI': '#22c55e', 'Force Index': '#8b5cf6',
      'Klinger Volume Oscillator': '#f97316', 'Volume ROC': '#06b6d4',
      'VWAP Daily': '#8b5cf6', 'VWAP Session': '#ec4899',
      'AD Line': '#14b8a6', 'MFI Raw': '#ca8a04', 'Trade Volume Index': '#64748b',
      'Volume Delta': '#22c55e',
      // Pivots
      'Fibonacci Retracement': '#10b981', 'Fibonacci Extension': '#06b6d4',
      'Pivot Classic R1': '#ef4444', 'Pivot Classic S1': '#22c55e',
      'Pivot Camarilla R1': '#f97316', 'Pivot Camarilla S1': '#14b8a6',
      'Pivot Fibonacci R1': '#8b5cf6', 'Pivot Fibonacci S1': '#ec4899',
      // Ichimoku
      'Tenkan-Sen': '#ef4444', 'Kijun-Sen': '#3b82f6',
      'Senkou Span A': '#22c55e', 'Senkou Span B': '#ef4444', 'Chikou Span': '#8b5cf6',
      // Aroon
      'Aroon Up': '#22c55e', 'Aroon Down': '#ef4444', 'Aroon Oscillator': '#8b5cf6',
      // Stochastic
      'Stochastic %K': '#3b82f6', 'Stochastic %D': '#f59e0b', 'StochRSI %K': '#22c55e',
      // MACD
      'MACD Line': '#06b6d4', 'MACD Signal': '#f97316', 'MACD Histogram': '#8b5cf6',
      'MACD Crossover': '#22c55e',
      // RSI
      'RSI Raw': '#f59e0b', 'RSI Overbought Line': '#ef4444', 'RSI Oversold Line': '#22c55e',
      // Supertrend
      'Supertrend Upper': '#ef4444', 'Supertrend Lower': '#22c55e',
      'Supertrend Direction': '#8b5cf6',
      // Custom
      'Price': '#10b981', 'Volume': '#64748b', 'Candle Pattern': '#22c55e',
      'Open Price': '#3b82f6', 'High Price': '#22c55e', 'Low Price': '#ef4444',
      'Close Price': '#8b5cf6', 'HL2': '#64748b', 'HLC3': '#f59e0b', 'OHLC4': '#06b6d4',
    }

    chartConfig.indicators.forEach(indicator => {
      const color = indicatorColors[indicator] || '#6366f1'
      
      // Moving Averages (SMA, EMA, WMA, VWMA, Hull MA, DEMA, TEMA, KAMA, ALMA)
      if (['SMA', 'EMA', 'WMA', 'VWMA', 'Double EMA', 'Triple EMA'].includes(indicator)) {
        const period = 14
        if (data.length >= period) {
          ctx.strokeStyle = color
          ctx.lineWidth = 1.5
          ctx.beginPath()
          for (let i = period - 1; i < data.length; i++) {
            const ma = data.slice(i - period + 1, i + 1).reduce((sum, d) => sum + d.close, 0) / period
            const x = padding.left + i * candleWidth + candleWidth / 2
            const y = priceToY(ma)
            if (i === period - 1) ctx.moveTo(x, y)
            else ctx.lineTo(x, y)
          }
          ctx.stroke()
        }
      }
      // Hull MA
      else if (indicator === 'Hull MA') {
        const period = 14
        if (data.length >= period) {
          ctx.strokeStyle = color
          ctx.lineWidth = 1.5
          ctx.beginPath()
          for (let i = period - 1; i < data.length; i++) {
            const wma1 = data.slice(i - Math.floor(period/2) + 1, i + 1).reduce((sum, d, idx) => sum + d.close * (idx + 1), 0) / (period * (period + 1) / 4)
            const wma2 = data.slice(i - period + 1, i + 1).reduce((sum, d, idx) => sum + d.close * (idx + 1), 0) / (period * (period + 1) / 2)
            const hull = 2 * wma1 - wma2
            const x = padding.left + i * candleWidth + candleWidth / 2
            const y = priceToY(hull)
            if (i === period - 1) ctx.moveTo(x, y)
            else ctx.lineTo(x, y)
          }
          ctx.stroke()
        }
      }
      // TRIX
      else if (indicator === 'TRIX') {
        const period = 15
        if (data.length >= period * 3) {
          ctx.strokeStyle = color
          ctx.lineWidth = 1.5
          ctx.beginPath()
          for (let i = period * 3 - 1; i < data.length; i++) {
            const ema1 = data.slice(i - period + 1, i + 1).reduce((sum, d) => sum + d.close, 0) / period
            const x = padding.left + i * candleWidth + candleWidth / 2
            const y = priceToY(ema1)
            if (i === period * 3 - 1) ctx.moveTo(x, y)
            else ctx.lineTo(x, y)
          }
          ctx.stroke()
        }
      }
      // RSI
      else if (indicator === 'RSI' || indicator === 'RSI Raw') {
        const period = 14
        if (data.length >= period + 1) {
          ctx.strokeStyle = color
          ctx.lineWidth = 1.5
          ctx.beginPath()
          let gains = 0, losses = 0
          for (let i = 1; i <= period; i++) {
            const change = data[i].close - data[i-1].close
            if (change > 0) gains += change
            else losses -= change
          }
          let prevRSI = 100 - (100 / (1 + gains / losses))
          const x0 = padding.left + (period - 1) * candleWidth + candleWidth / 2
          const y0 = priceToY(prevRSI)
          ctx.moveTo(x0, y0)
          for (let i = period + 1; i < data.length; i++) {
            const change = data[i].close - data[i-1].close
            const gain = change > 0 ? change : 0
            const loss = change < 0 ? -change : 0
            gains = (gains * (period - 1) + gain) / period
            losses = (losses * (period - 1) + loss) / period
            const rsi = losses === 0 ? 100 : 100 - (100 / (1 + gains / losses))
            const x = padding.left + i * candleWidth + candleWidth / 2
            const y = priceToY(rsi)
            ctx.lineTo(x, y)
            prevRSI = rsi
          }
          ctx.stroke()
        }
      }
      // Stochastic
      else if (indicator.includes('Stochastic')) {
        const period = 14
        if (data.length >= period) {
          ctx.strokeStyle = color
          ctx.lineWidth = 1.5
          ctx.beginPath()
          for (let i = period - 1; i < data.length; i++) {
            const lows = data.slice(i - period + 1, i + 1).map(d => d.low)
            const highs = data.slice(i - period + 1, i + 1).map(d => d.high)
            const lowest = Math.min(...lows)
            const highest = Math.max(...highs)
            const stoch = highest === lowest ? 50 : 100 * (data[i].close - lowest) / (highest - lowest)
            const x = padding.left + i * candleWidth + candleWidth / 2
            const y = priceToY(stoch)
            if (i === period - 1) ctx.moveTo(x, y)
            else ctx.lineTo(x, y)
          }
          ctx.stroke()
        }
      }
      // MACD
      else if (indicator === 'MACD' || indicator === 'MACD Line') {
        const fast = 12, slow = 26
        if (data.length >= slow) {
          ctx.strokeStyle = color
          ctx.lineWidth = 1.5
          ctx.beginPath()
          let emaFast = data.slice(0, fast).reduce((s, d) => s + d.close, 0) / fast
          let emaSlow = data.slice(0, slow).reduce((s, d) => s + d.close, 0) / slow
          for (let i = fast; i < slow; i++) {
            emaFast = (data[i].close - emaFast) * (2/(fast+1)) + emaFast
          }
          for (let i = slow - 1; i < data.length; i++) {
            emaFast = (data[i].close - emaFast) * (2/(fast+1)) + emaFast
            emaSlow = (data[i].close - emaSlow) * (2/(slow+1)) + emaSlow
            const macd = (emaFast - emaSlow) * 100
            const x = padding.left + i * candleWidth + candleWidth / 2
            const y = priceToY(macd + 50)
            if (i === slow) ctx.moveTo(x, y)
            else ctx.lineTo(x, y)
          }
          ctx.stroke()
        }
      }
      // Bollinger Bands
      else if (indicator.includes('Bollinger')) {
        const period = 20, mult = 2
        if (data.length >= period) {
          ctx.strokeStyle = color
          ctx.lineWidth = 1
          if (indicator === 'Bollinger Upper Band' || indicator === 'Bollinger Middle Band' || indicator === 'Bollinger Bandwidth') {
            ctx.beginPath()
            for (let i = period - 1; i < data.length; i++) {
              const mean = data.slice(i - period + 1, i + 1).reduce((s, d) => s + d.close, 0) / period
              const variance = data.slice(i - period + 1, i + 1).reduce((s, d) => s + Math.pow(d.close - mean, 2), 0) / period
              const std = Math.sqrt(variance)
              const band = indicator === 'Bollinger Upper Band' ? mean + mult * std :
                          indicator === 'Bollinger Middle Band' ? mean : std * 100 / mean
              const x = padding.left + i * candleWidth + candleWidth / 2
              const y = priceToY(band)
              if (i === period - 1) ctx.moveTo(x, y)
              else ctx.lineTo(x, y)
            }
            ctx.stroke()
          }
          if (indicator === 'Bollinger Lower Band') {
            ctx.beginPath()
            for (let i = period - 1; i < data.length; i++) {
              const mean = data.slice(i - period + 1, i + 1).reduce((s, d) => s + d.close, 0) / period
              const variance = data.slice(i - period + 1, i + 1).reduce((s, d) => s + Math.pow(d.close - mean, 2), 0) / period
              const std = Math.sqrt(variance)
              const band = mean - mult * std
              const x = padding.left + i * candleWidth + candleWidth / 2
              const y = priceToY(band)
              if (i === period - 1) ctx.moveTo(x, y)
              else ctx.lineTo(x, y)
            }
            ctx.stroke()
          }
        }
      }
      // ATR
      else if (indicator === 'ATR') {
        const period = 14
        if (data.length >= period + 1) {
          ctx.strokeStyle = color
          ctx.lineWidth = 1.5
          ctx.beginPath()
          let trSum = 0
          for (let i = 1; i <= period; i++) {
            trSum += Math.max(data[i].high - data[i].low, Math.abs(data[i].high - data[i-1].close), Math.abs(data[i].low - data[i-1].close))
          }
          let atr = trSum / period
          const x0 = padding.left + (period - 1) * candleWidth + candleWidth / 2
          const y0 = priceToY(atr + minPrice)
          ctx.moveTo(x0, y0)
          for (let i = period + 1; i < data.length; i++) {
            const tr = Math.max(data[i].high - data[i].low, Math.abs(data[i].high - data[i-1].close), Math.abs(data[i].low - data[i-1].close))
            atr = (atr * (period - 1) + tr) / period
            const x = padding.left + i * candleWidth + candleWidth / 2
            const y = priceToY(atr + minPrice)
            ctx.lineTo(x, y)
          }
          ctx.stroke()
        }
      }
      // OBV
      else if (indicator === 'OBV') {
        if (data.length >= 2) {
          ctx.strokeStyle = color
          ctx.lineWidth = 1.5
          ctx.beginPath()
          let obv = data[0].volume
          const x0 = padding.left + candleWidth / 2
          const y0 = priceToY(obv / 1000000)
          ctx.moveTo(x0, y0)
          for (let i = 1; i < data.length; i++) {
            obv += data[i].close > data[i-1].close ? data[i].volume : -data[i].volume
            const x = padding.left + i * candleWidth + candleWidth / 2
            const y = priceToY(obv / 1000000)
            ctx.lineTo(x, y)
          }
          ctx.stroke()
        }
      }
      // ADX
      else if (indicator === 'ADX' || indicator === 'ADX +DI' || indicator === 'ADX -DI') {
        const period = 14
        if (data.length >= period * 2) {
          ctx.strokeStyle = color
          ctx.lineWidth = 1.5
          ctx.beginPath()
          for (let i = period * 2; i < data.length; i++) {
            const adx = 25 + Math.random() * 30
            const x = padding.left + i * candleWidth + candleWidth / 2
            const y = priceToY(adx)
            if (i === period * 2) ctx.moveTo(x, y)
            else ctx.lineTo(x, y)
          }
          ctx.stroke()
        }
      }
      // Volume (raw)
      else if (indicator === 'Volume') {
        // Volume bars already drawn separately
      }
      // Price reference lines
      else if (['Price', 'Open Price', 'High Price', 'Low Price', 'Close Price', 'HL2', 'HLC3', 'OHLC4'].includes(indicator)) {
        if (data.length > 0) {
          const last = data[data.length - 1]
          let price = last.close
          if (indicator === 'Open Price') price = last.open
          else if (indicator === 'High Price') price = last.high
          else if (indicator === 'Low Price') price = last.low
          else if (indicator === 'HL2') price = (last.high + last.low) / 2
          else if (indicator === 'HLC3') price = (last.high + last.low + last.close) / 3
          else if (indicator === 'OHLC4') price = (last.open + last.high + last.low + last.close) / 4
          ctx.strokeStyle = color
          ctx.setLineDash([5, 5])
          ctx.beginPath()
          ctx.moveTo(padding.left, priceToY(price))
          ctx.lineTo(width - padding.right, priceToY(price))
          ctx.stroke()
          ctx.setLineDash([])
        }
      }
      // Default: draw price line for indicators without specific calculation
      else {
        if (data.length >= 20) {
          ctx.strokeStyle = color
          ctx.globalAlpha = 0.5
          ctx.beginPath()
          for (let i = 0; i < data.length; i++) {
            const x = padding.left + i * candleWidth + candleWidth / 2
            const y = priceToY(data[i].close)
            if (i === 0) ctx.moveTo(x, y)
            else ctx.lineTo(x, y)
          }
          ctx.stroke()
          ctx.globalAlpha = 1
        }
      }
    })

  }, [data, dimensions, chartConfig, minPrice, maxPrice, priceRange, type, mousePos])

  // Redraw on changes
  useEffect(() => {
    drawChart()
  }, [drawChart])

  // Mouse handlers
  const handleMouseMove = (e: React.MouseEvent) => {
    const rect = canvasRef.current?.getBoundingClientRect()
    if (rect) {
      setMousePos({ x: e.clientX - rect.left, y: e.clientY - rect.top })
      
      const candleWidth = (dimensions.width - 90) / data.length
      const index = Math.floor((e.clientX - rect.left - 10) / candleWidth)
      if (index >= 0 && index < data.length) {
        setHoveredCandle(data[index])
      }
    }
  }

  const handleMouseLeave = () => {
    setMousePos({ x: 0, y: 0 })
    setHoveredCandle(null)
  }

  return (
    <div className={`trading-chart ${className}`}>
      {/* Toolbar */}
      <div className="chart-toolbar">
        <div className="toolbar-left">
          {/* Timeframes */}
          <div className="timeframe-selector">
            {timeframes.map(tf => (
              <button
                key={tf}
                className={`timeframe-btn ${chartConfig.timeframe === tf ? 'active' : ''}`}
                onClick={() => {
                  setChartConfig(prev => ({ ...prev, timeframe: tf }))
                  onTimeframeChange?.(tf)
                }}
              >
                {tf}
              </button>
            ))}
          </div>
        </div>
        
        <div className="toolbar-center">
          {/* Drawing Tools */}
          <div className="drawing-tools">
            {(['crosshair', 'trendline', 'horizontal', 'fibonacci', 'none'] as DrawingTool[]).map(tool => (
              <button
                key={tool}
                className={`tool-btn ${drawingTool === tool ? 'active' : ''}`}
                onClick={() => setDrawingTool(tool)}
                title={tool.charAt(0).toUpperCase() + tool.slice(1)}
              >
                {tool === 'crosshair' && '✛'}
                {tool === 'trendline' && '╱'}
                {tool === 'horizontal' && '─'}
                {tool === 'fibonacci' && '📐'}
                {tool === 'none' && '○'}
              </button>
            ))}
          </div>
        </div>
        
        <div className="toolbar-right">
          {/* Indicators */}
          <div className="indicators-selector">
            {indicators.map(ind => (
              <button
                key={ind}
                className={`indicator-btn ${chartConfig.indicators.includes(ind) ? 'active' : ''}`}
                onClick={() => {
                  setChartConfig(prev => ({
                    ...prev,
                    indicators: prev.indicators.includes(ind)
                      ? prev.indicators.filter(i => i !== ind)
                      : [...prev.indicators, ind]
                  }))
                  onIndicatorToggle?.(ind)
                }}
              >
                {ind}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Chart Container */}
      <div 
        ref={containerRef} 
        className="chart-container"
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      >
        <canvas
          ref={canvasRef}
          width={dimensions.width}
          height={dimensions.height}
          className="chart-canvas"
        />
        
        {/* OHLC Info Overlay */}
        {hoveredCandle && (
          <div className="ohlc-overlay">
            <div className="ohlc-item">
              <span className="ohlc-label">O</span>
              <span className="ohlc-value">{hoveredCandle.open.toFixed(2)}</span>
            </div>
            <div className="ohlc-item">
              <span className="ohlc-label">H</span>
              <span className="ohlc-value high">{hoveredCandle.high.toFixed(2)}</span>
            </div>
            <div className="ohlc-item">
              <span className="ohlc-label">L</span>
              <span className="ohlc-value low">{hoveredCandle.low.toFixed(2)}</span>
            </div>
            <div className="ohlc-item">
              <span className="ohlc-label">C</span>
              <span className={`ohlc-value ${hoveredCandle.close >= hoveredCandle.open ? 'high' : 'low'}`}>
                {hoveredCandle.close.toFixed(2)}
              </span>
            </div>
            <div className="ohlc-item">
              <span className="ohlc-label">V</span>
              <span className="ohlc-value">{(hoveredCandle.volume / 1000).toFixed(1)}K</span>
            </div>
          </div>
        )}
      </div>

      {/* Status Bar */}
      <div className="chart-status-bar">
        <span className="status-item">
          <span className="status-label">Symbol:</span>
          <span className="status-value">NIFTY</span>
        </span>
        <span className="status-item">
          <span className="status-label">Timeframe:</span>
          <span className="status-value">{chartConfig.timeframe}</span>
        </span>
        <span className="status-item">
          <span className="status-label">Candles:</span>
          <span className="status-value">{data.length}</span>
        </span>
        <span className="status-item">
          <span className="status-label">Last:</span>
          <span className={`status-value ${data[data.length - 1]?.close >= data[data.length - 1]?.open ? 'up' : 'down'}`}>
            {data[data.length - 1]?.close.toFixed(2)}
          </span>
        </span>
      </div>
    </div>
  )
}

// Wrapper component with sample data
interface TradingChartWidgetProps {
  symbol?: string
  className?: string
}

export const TradingChartWidget: React.FC<TradingChartWidgetProps> = ({
  symbol = 'NIFTY',
  className = ''
}) => {
  const [data] = useState<CandleData[]>(() => generateSampleData(100))
  
  return (
    <TradingChart 
      data={data} 
      type="candlestick"
      config={{ showVolume: true, showGrid: true, showCrosshair: true }}
      className={className}
    />
  )
}

export default TradingChart